/**
 * nami:notify handler (A3-T2 Reach v1) — the live leg of the reach rail.
 *
 * THE PARSE IS THE GATE. The server emits this frame without validating it on
 * the way out, and the surface downstream raises OS-level banners, so a
 * malformed or over-long frame has to die here rather than being rendered half
 * way. The schema is the shared one the server's own contract is derived from,
 * so "what the wire promises" has exactly one author.
 */

import { EVENTS, NamiNotifyPayloadSchema } from "@bash/shared";
import type { Socket } from "socket.io-client";
import { useReachStore } from "../reachStore";
import { useComposerHistoryStore } from "../composerHistoryStore";
import { composerRowOf } from "./composerFrameRow";

export function registerReachHandlers(socket: Socket): () => void {
  const handleNotify = (data: unknown) => {
    const parsed = NamiNotifyPayloadSchema.safeParse(data);
    // Dropped, not repaired: a frame that does not match the contract has no
    // trustworthy title, body, or deep link, and guessing at one of them is how
    // a notification sends the user somewhere nobody asked for.
    if (!parsed.success) return;
    // Spec 2026-09-18 §5.4: a `composer` frame is Bash speaking first, so its words join the
    // composer's history here, live, and fold into the server's own row at the next fetch.
    const row = composerRowOf(parsed.data);
    if (row) useComposerHistoryStore.getState().add(row);
    useReachStore.getState().push(parsed.data);
  };

  socket.on(EVENTS.NAMI_NOTIFY, handleNotify);

  return () => {
    socket.off(EVENTS.NAMI_NOTIFY, handleNotify);
  };
}
